import { useState, useEffect } from 'react';
import { AlertTriangle, XCircle, X, Info } from 'lucide-react';
import { SubscriptionDto } from '@optidev/shared-types';
import { AddFundsModal } from '@/routes/dashboard/billing/AddFundsModal';

interface BillingWarningBannerProps {
  subscription: SubscriptionDto | null;
}

const DISMISSED_KEY = 'billing-banner-dismissed';
const DISMISS_DURATION_LOW = 48 * 60 * 60 * 1000; // 48 hours for low balance
const DISMISS_DURATION_NEGATIVE = 24 * 60 * 60 * 1000; // 24 hours for negative
const DISMISS_DURATION_SUSPENDED = 4 * 60 * 60 * 1000; // 4 hours for suspended

/**
 * Billing Warning Banner
 * Shows warning when cloud balance is low/negative, or error when suspended.
 * Displays at top of all dashboard pages. Can be dismissed by user.
 */
export function BillingWarningBanner({ subscription }: BillingWarningBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);

  const balanceWarning = subscription?.balanceWarning;
  const gracePeriodEnd = subscription?.gracePeriodEnd;
  const suspended = subscription?.suspended;

  // Determine current state for dismiss logic
  const getCurrentState = () => {
    if (suspended) return 'suspended';
    if (balanceWarning === 'negative') return 'negative';
    if (balanceWarning === 'low') return 'low';
    return null;
  };

  const getDismissDuration = (state: string | null) => {
    switch (state) {
      case 'suspended': return DISMISS_DURATION_SUSPENDED;
      case 'negative': return DISMISS_DURATION_NEGATIVE;
      case 'low': return DISMISS_DURATION_LOW;
      default: return DISMISS_DURATION_LOW;
    }
  };

  // Check if banner was previously dismissed
  useEffect(() => {
    const currentState = getCurrentState();
    if (!currentState) return;

    const dismissedData = localStorage.getItem(DISMISSED_KEY);
    if (dismissedData) {
      try {
        const { state, timestamp } = JSON.parse(dismissedData);
        const duration = getDismissDuration(currentState);

        // Re-show if state changed or duration expired
        if (state === currentState && Date.now() - timestamp < duration) {
          setIsDismissed(true);
        } else {
          localStorage.removeItem(DISMISSED_KEY);
          setIsDismissed(false);
        }
      } catch {
        localStorage.removeItem(DISMISSED_KEY);
      }
    }
  }, [suspended, balanceWarning]);

  if (!subscription) return null;

  // No warning needed if balance is healthy
  if (!balanceWarning && !suspended) return null;

  // Don't show if dismissed
  if (isDismissed) return null;

  // Format deadline date
  const formatDeadline = (isoString: string | null | undefined): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Open add funds modal
  const handleAddFunds = () => {
    setShowAddFundsModal(true);
  };

  // Dismiss the banner
  const handleDismiss = () => {
    const state = getCurrentState();
    localStorage.setItem(DISMISSED_KEY, JSON.stringify({ state, timestamp: Date.now() }));
    setIsDismissed(true);
  };

  // Suspended state - red banner
  if (suspended) {
    return (
      <div className="bg-red-500 dark:bg-red-600 text-white px-4 py-3">
        <div className="flex items-center justify-between gap-4 ">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              Your published projects are paused due to insufficient balance. Add funds to resume.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddFunds}
              className="px-4 py-1.5 bg-white text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
            >
              Add Funds
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-red-400 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <AddFundsModal isOpen={showAddFundsModal} onClose={() => setShowAddFundsModal(false)} />
      </div>
    );
  }

  // Warning state (negative balance, in grace period) - yellow/amber banner
  if (balanceWarning === 'negative' && gracePeriodEnd) {
    return (
      <div className="bg-amber-500 dark:bg-amber-600 text-white px-4 py-3">
        <div className="flex items-center justify-between gap-4 ">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              Your cloud balance is negative. Add funds by {formatDeadline(gracePeriodEnd)} to avoid suspension.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddFunds}
              className="px-4 py-1.5 bg-white text-amber-600 hover:bg-amber-50 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
            >
              Add Funds
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-amber-400 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <AddFundsModal isOpen={showAddFundsModal} onClose={() => setShowAddFundsModal(false)} />
      </div>
    );
  }

  // Low balance warning (0 < balance < $5) - blue/info banner
  if (balanceWarning === 'low') {
    return (
      <div className="bg-blue-500 dark:bg-blue-600 text-white px-4 py-3">
        <div className="flex items-center justify-between gap-4 ">
          <div className="flex items-center gap-3">
            <Info className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              Your cloud balance is running low. Consider adding funds to avoid interruption.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddFunds}
              className="px-4 py-1.5 bg-white text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
            >
              Add Funds
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-blue-400 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <AddFundsModal isOpen={showAddFundsModal} onClose={() => setShowAddFundsModal(false)} />
      </div>
    );
  }

  return null;
}
